import { ButtonGroup, Flex, IconButton, Text } from "@chakra-ui/react";
import { RiArrowLeftSLine, RiArrowRightSLine } from "@remixicon/react";
import { Table } from "@tanstack/react-table";

import CompactNumber from "@/common/components/CompactNumber";
import { getFetchingFadeProps } from "@/common/components/DataTable/DataTable";
import MotionBox from "@/common/components/MotionBox";
import { formatNumber } from "@/common/utils/numberFormatting";
import { getFraction } from "@reflag/shared/utils/getFraction";

export default function DataTablePagination<TData>({
  isLoading,
  pageSize,
  pageIndex,
  totalCount,
  nextPage,
  getCanNextPage,
  previousPage,
  getCanPreviousPage,
  getRowModel,
}: Table<TData> & {
  pageSize: number;
  pageIndex: number;
  totalCount?: number;
  isLoading?: boolean;
}) {
  const currentPageRowCount = getRowModel().rows.length;
  const hasKnownTotal = totalCount !== undefined;
  const pageCount = hasKnownTotal
    ? Math.ceil(getFraction(totalCount, pageSize))
    : undefined;
  const rangeStart = currentPageRowCount > 0 ? pageIndex * pageSize + 1 : 0;
  const rangeEnd =
    currentPageRowCount > 0 ? pageIndex * pageSize + currentPageRowCount : 0;
  const canPreviousPageWhenCountUnknown = pageIndex > 0;
  const canNextPageWhenCountUnknown = currentPageRowCount >= pageSize;
  const showControls = hasKnownTotal
    ? totalCount > pageSize
    : canPreviousPageWhenCountUnknown || canNextPageWhenCountUnknown;
  const isPreviousDisabled = hasKnownTotal
    ? !getCanPreviousPage()
    : !canPreviousPageWhenCountUnknown;
  const isNextDisabled = hasKnownTotal
    ? !getCanNextPage()
    : !canNextPageWhenCountUnknown;

  const hasPageInfo =
    pageCount !== undefined &&
    pageIndex !== undefined &&
    pageSize !== undefined &&
    hasKnownTotal;

  return (
    <Flex align="center" as="nav">
      <Text
        color="dimmed"
        fontSize="sm"
        mr={2}
        whiteSpace="nowrap"
        {...getFetchingFadeProps(isLoading)}
      >
        {hasPageInfo ? (
          pageCount > 1 ? (
            <>
              {`${formatNumber(
                Math.min(pageIndex * pageSize + 1, totalCount),
              )} to ${formatNumber(
                Math.min(pageSize * pageIndex + pageSize, totalCount),
              )} of `}
              <CompactNumber value={totalCount} />
            </>
          ) : (
            <>
              <CompactNumber value={totalCount} /> of{" "}
              <CompactNumber value={totalCount} />
            </>
          )
        ) : currentPageRowCount > 0 ? (
          `${formatNumber(rangeStart)} to ${formatNumber(rangeEnd)}`
        ) : null}
      </Text>
      {showControls && (
        <MotionBox
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          <ButtonGroup size="sm" spacing={0} variant="ghost">
            <IconButton
              aria-label="Previous page"
              icon={<RiArrowLeftSLine size={18} />}
              isDisabled={isPreviousDisabled}
              onClick={() => {
                previousPage();
              }}
            />
            <IconButton
              aria-label="Next page"
              icon={<RiArrowRightSLine size={18} />}
              isDisabled={isNextDisabled}
              onClick={() => {
                nextPage();
              }}
            />
          </ButtonGroup>
        </MotionBox>
      )}
    </Flex>
  );
}
